import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  apiMe, apiLogin, apiRegister, apiLogout, apiGoogleSession,
  getToken, setToken,
} from "@/lib/inmindApi";

const AuthCtx = createContext({ user: null, loading: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If returning from OAuth callback, skip /auth/me — AuthCallback handles it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      // Try with whatever credentials we have (JWT in localStorage OR session_token cookie).
      try {
        const u = await apiMe();
        setUser(u);
      } catch {
        if (getToken()) setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const u = await apiLogin({ email, password });
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (payload) => {
    const u = await apiRegister(payload);
    setUser(u);
    return u;
  }, []);

  const loginWithGoogle = useCallback(async (sessionId) => {
    const u = await apiGoogleSession(sessionId);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, loginWithGoogle, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
